import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiMapPin, FiCheckCircle } from 'react-icons/fi';
import {
  PageContainer, Container,
  HeadingSerif, BodyText, ButtonSolid, ButtonOutline,
  PreTitle,
} from './styles/MFRealStateStyles';
import {
  VisitLayout, VisitMain, VisitAsideCard, VisitAsideImg, VisitAsideInfo,
  VisitStep, DatePickerRow, DateChip, TimeSlotGrid, TimeSlot,
  VisitSummary, VisitSummaryItem, VisitInfoBoxes, VisitInfoBox,
  VisitForm, VisitField, VisitFooter, VisitSuccess,
} from './styles/MFRealStateStyles';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { getPropertyById, formatPrice, buildPropertySlug } from './services/propertyService';
import { sortearCorretor } from './services/imovelCorretoresService';
import { supabase } from '../../services/supabaseClient';
import { MF_BASE } from './basePath';

const DIAS_SEMANA_CURTO = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DIAS_SEMANA_LONGO = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

function buildNextDays(count = 14) {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    // pula domingos
    if (d.getDay() === 0) continue;
    out.push(d);
  }
  return out;
}

function buildSlots() {
  // 9:00 → 18:00 de 30 em 30
  const out = [];
  for (let h = 9; h < 18; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    out.push(`${String(h).padStart(2, '0')}:30`);
  }
  out.push('18:00');
  return out;
}

function formatHumanDate(d) {
  return `${DIAS_SEMANA_LONGO[d.getDay()]}, ${d.getDate()} de ${MESES[d.getMonth()]}`;
}

export default function VisitAgendarPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leadPhone, setLeadPhone] = useState('');

  const [step, setStep] = useState(1); // 1 = data/hora, 2 = contato, 3 = sucesso
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedTime, setPickedTime] = useState(null);

  const [form, setForm] = useState({ nome: '', telefone: '', email: '', mensagem: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const days = useMemo(() => buildNextDays(14), []);
  const slots = useMemo(() => buildSlots(), []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const p = await getPropertyById(id);
      setProperty(p);
      setLoading(false);
    }
    load();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    supabase.from('mf_config').select('telefone_encaminhamento_leads').eq('id', 1).single()
      .then(({ data }) => {
        if (data?.telefone_encaminhamento_leads) setLeadPhone(data.telefone_encaminhamento_leads);
      });
  }, []);

  function validateContact() {
    const e = {};
    if (!form.nome.trim()) e.nome = 'Informe seu nome';
    if (!form.telefone.trim() || form.telefone.replace(/\D/g, '').length < 10) e.telefone = 'Telefone inválido';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'E-mail inválido';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (!validateContact()) return;
    setSubmitting(true);

    const dataHumana = formatHumanDate(pickedDate);
    const enderecoStr = [property.endereco, property.bairro, property.cidade].filter(Boolean).join(', ');

    // Monta agendamento estruturado pra task backend formatar a notificação
    const agendamentoPayload = {
      imovel_id: property.id,
      imovel_titulo: property.titulo,
      data_humana: dataHumana,
      data_iso: pickedDate.toISOString().slice(0, 10),
      horario: pickedTime,
      endereco: enderecoStr,
      mensagem: form.mensagem.trim() || null,
    };

    // 1. Insere lead no Supabase
    const { data: lead, error: leadErr } = await supabase
      .from('mf_leads_site')
      .insert({
        nome: form.nome.trim(),
        telefone: form.telefone.trim(),
        email: form.email.trim() || null,
        origem: 'visita_imovel',
        agendamento: JSON.stringify(agendamentoPayload),
      })
      .select('id')
      .single();

    if (leadErr) {
      console.error('Erro ao salvar lead:', leadErr);
    }

    // 2. Dispara notificação via Trigger.dev (fire-and-forget)
    if (lead?.id) {
      fetch('/api/mf-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: lead.id, evento: 'agendamento_visita_imovel' }),
      }).catch(() => {});
    }

    // 3. Abre WhatsApp — sorteia corretor se imóvel da imobiliária
    let destPhone = leadPhone || property?.corretor_telefone;
    if (!property?.corretor_id && !destPhone) {
      const sorteado = await sortearCorretor(property.id);
      if (sorteado?.telefone) destPhone = sorteado.telefone;
    }
    destPhone = destPhone || '5511999999999';
    const msg = [
      `Olá! Acabei de solicitar uma visita pelo site:`,
      '',
      `*${property.titulo}*`,
      `${dataHumana} às ${pickedTime}`,
      '',
      `Nome: ${form.nome}`,
      `Telefone: ${form.telefone}`,
      form.mensagem ? `\n${form.mensagem}` : null,
    ].filter(Boolean).join('\n');

    const url = `https://wa.me/${destPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');

    setStep(3);
    setSubmitting(false);
  }

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <Container style={{ paddingTop: 120, textAlign: 'center', minHeight: '60vh' }}>
          <BodyText $muted>Carregando...</BodyText>
        </Container>
        <Footer />
      </PageContainer>
    );
  }

  if (!property) {
    return (
      <PageContainer>
        <Navbar />
        <Container style={{ paddingTop: 120, textAlign: 'center', minHeight: '60vh' }}>
          <HeadingSerif $size="md">Imóvel não encontrado</HeadingSerif>
          <ButtonOutline onClick={() => navigate(`${MF_BASE}/imoveis`)}>
            Ver todos os imóveis
          </ButtonOutline>
        </Container>
        <Footer />
      </PageContainer>
    );
  }

  const cover = property.imagens?.[0];
  const propertyHref = `${MF_BASE}/imoveis/${buildPropertySlug(property)}`;

  return (
    <PageContainer>
      <Navbar />

      <Container style={{ paddingTop: 100, paddingBottom: 80, minHeight: '70vh' }}>
        <Link
          to={propertyHref}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            textDecoration: 'none', color: '#535C70',
            fontFamily: 'Inter, sans-serif', fontSize: 14, marginBottom: 24,
          }}
        >
          <FiArrowLeft /> Voltar ao imóvel
        </Link>

        <VisitLayout>
          <VisitMain>
            {step === 1 && (
              <VisitStep>
                <HeadingSerif $size="md" style={{ marginTop: 0 }}>
                  Quando você quer visitar este imóvel?
                </HeadingSerif>

                <PreTitle style={{ marginTop: 24 }}>Escolha um dia</PreTitle>
                <DatePickerRow>
                  {days.map((d) => {
                    const selected = pickedDate && pickedDate.toDateString() === d.toDateString();
                    return (
                      <DateChip
                        key={d.toISOString()}
                        $selected={selected}
                        type="button"
                        onClick={() => setPickedDate(d)}
                      >
                        <span className="dow">{DIAS_SEMANA_CURTO[d.getDay()]}</span>
                        <span className="day">{d.getDate()}</span>
                      </DateChip>
                    );
                  })}
                </DatePickerRow>

                <PreTitle style={{ marginTop: 32 }}>Escolha um horário</PreTitle>
                <TimeSlotGrid>
                  {slots.map((t) => (
                    <TimeSlot
                      key={t}
                      $selected={pickedTime === t}
                      type="button"
                      onClick={() => setPickedTime(t)}
                    >
                      {t}
                    </TimeSlot>
                  ))}
                </TimeSlotGrid>

                <VisitFooter>
                  <ButtonSolid
                    $fullWidth
                    disabled={!pickedDate || !pickedTime}
                    onClick={() => setStep(2)}
                  >
                    Continuar
                  </ButtonSolid>
                </VisitFooter>
              </VisitStep>
            )}

            {step === 2 && (
              <VisitStep>
                <HeadingSerif $size="md" style={{ marginTop: 0 }}>
                  Podemos solicitar esta visita?
                </HeadingSerif>

                <VisitSummary>
                  <VisitSummaryItem>
                    <FiCalendar />
                    <div>
                      <div className="label">Dia e horário</div>
                      <div className="value">
                        {formatHumanDate(pickedDate)} às {pickedTime}
                      </div>
                    </div>
                  </VisitSummaryItem>
                  <VisitSummaryItem>
                    <FiMapPin />
                    <div>
                      <div className="label">Endereço</div>
                      <div className="value">
                        {[property.endereco, property.bairro, property.cidade].filter(Boolean).join(' — ')}
                      </div>
                    </div>
                  </VisitSummaryItem>
                </VisitSummary>

                <VisitInfoBoxes>
                  <VisitInfoBox>
                    Um corretor da MF vai organizar a visita e pode tirar suas dúvidas.
                  </VisitInfoBox>
                  <VisitInfoBox>
                    Após enviar, entramos em contato por WhatsApp pra confirmar.
                  </VisitInfoBox>
                </VisitInfoBoxes>

                <VisitForm onSubmit={handleSubmit}>
                  <PreTitle>Seus dados</PreTitle>
                  <VisitField $invalid={!!errors.nome}>
                    <label htmlFor="nome">Nome completo</label>
                    <input
                      id="nome"
                      type="text"
                      autoComplete="name"
                      value={form.nome}
                      onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))}
                    />
                    {errors.nome && <span className="err">{errors.nome}</span>}
                  </VisitField>

                  <VisitField $invalid={!!errors.telefone}>
                    <label htmlFor="telefone">Telefone (WhatsApp)</label>
                    <input
                      id="telefone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="(11) 99999-9999"
                      value={form.telefone}
                      onChange={(e) => setForm(f => ({ ...f, telefone: e.target.value }))}
                    />
                    {errors.telefone && <span className="err">{errors.telefone}</span>}
                  </VisitField>

                  <VisitField $invalid={!!errors.email}>
                    <label htmlFor="email">E-mail</label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    />
                    {errors.email && <span className="err">{errors.email}</span>}
                  </VisitField>

                  <VisitField>
                    <label htmlFor="mensagem">Mensagem (opcional)</label>
                    <textarea
                      id="mensagem"
                      rows={3}
                      placeholder="Alguma observação para o corretor?"
                      value={form.mensagem}
                      onChange={(e) => setForm(f => ({ ...f, mensagem: e.target.value }))}
                    />
                  </VisitField>

                  <VisitFooter>
                    <ButtonOutline type="button" onClick={() => setStep(1)}>
                      Voltar
                    </ButtonOutline>
                    <ButtonSolid type="submit" disabled={submitting}>
                      {submitting ? 'Enviando...' : 'Solicitar visita'}
                    </ButtonSolid>
                  </VisitFooter>
                </VisitForm>
              </VisitStep>
            )}

            {step === 3 && (
              <VisitSuccess>
                <FiCheckCircle />
                <HeadingSerif $size="md" style={{ marginTop: 16 }}>
                  Visita solicitada!
                </HeadingSerif>
                <BodyText>
                  Abrimos o WhatsApp pra você confirmar o agendamento com nossa equipe.
                  Se a janela não abriu,{' '}
                  <button
                    type="button"
                    onClick={() => handleSubmit({ preventDefault: () => {} })}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                  >
                    clique aqui
                  </button>.
                </BodyText>
                <ButtonOutline as={Link} to={propertyHref} style={{ marginTop: 24 }}>
                  Voltar ao imóvel
                </ButtonOutline>
              </VisitSuccess>
            )}
          </VisitMain>

          <VisitAsideCard>
            {cover && (
              <Link to={propertyHref}>
                <VisitAsideImg src={cover} alt={property.titulo} />
              </Link>
            )}
            <VisitAsideInfo>
              <div className="title">{property.titulo}</div>
              {property.preco && (
                <div className="price">{formatPrice(property.preco)}</div>
              )}
              {(property.area_m2 || property.quartos) && (
                <div className="specs">
                  {[
                    property.area_m2 && `${property.area_m2} m²`,
                    property.quartos && `${property.quartos} quartos`,
                    property.vagas && `${property.vagas} vagas`,
                  ].filter(Boolean).join(' · ')}
                </div>
              )}
              {(property.endereco || property.bairro) && (
                <div className="address">
                  {[property.endereco, property.bairro, property.cidade].filter(Boolean).join(', ')}
                </div>
              )}
            </VisitAsideInfo>
          </VisitAsideCard>
        </VisitLayout>
      </Container>

      <Footer />
    </PageContainer>
  );
}
